import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Nil } from '@pbt/pbt-ui-components'
import { PermissionArea, Utils } from '@pbt/pbt-ui-components'

import FeatureToggle from '~/constants/featureToggle'
import { PrescriptionStates } from '~/constants/prescription'
import { OrderType } from '~/constants/SOAPStates'
import {
  cancelRefills,
  editOrder,
  setPrescriptionForPrint,
} from '~/store/actions/orders'
import { useOpenInvoice } from '~/store/hooks/finance'
import {
  useOpenPrescriptionDialogAfterFetchUnifiedOrder,
  useRefillDialog,
} from '~/store/hooks/orders'
import { getCRUDByArea, getCurrentBusinessId } from '~/store/reducers/auth'
import {
  getFeatureToggle,
  getPrescriptionStates,
} from '~/store/reducers/constants'
import { getOrdersIsSending, getUnifiedOrder } from '~/store/reducers/orders'
import { getPatient } from '~/store/reducers/patients'
import { getUser } from '~/store/reducers/users'
import type {
  CRUDOrderOptions,
  Order,
  Prescription,
  PrescriptionTimelineItem,
} from '~/types'
import { addOriginalBusinessId } from '~/utils'
import { getPrescriptionWorkflowType } from '~/utils/prescription'
import {
  getIdFromTimelineItem,
  timelinePrescriptionToRegularPrescription,
} from '~/utils/prescriptionUtils'
import { usePersistClickedItemOnURL } from '~/utils/routes'
import useIsCurrentContextItem from '~/utils/useIsCurrentContextItem'

import { prepareRefillPrescription } from './rxTracker/rxCardUtilities'

export interface UsePrescriptionCardActionsProps {
  index?: number
  isFoodCatalogEnabled?: boolean
  isInvoice?: boolean
  item: PrescriptionTimelineItem
  onUpdateRefill?: (refill: Order, options: CRUDOrderOptions) => void
  refill?: Prescription
  showPrint?: boolean
}

export interface ActionVisibility {
  canShowMenu: boolean
  isChewy: boolean
  shouldShowInvoice: boolean
  shouldShowPreview: boolean
  shouldShowPrint: boolean
  shouldShowSoap: boolean
}

export const usePrescriptionCardActions = ({
  index = 0,
  isFoodCatalogEnabled,
  isInvoice = false,
  item,
  onUpdateRefill,
  refill,
  showPrint = true,
}: UsePrescriptionCardActionsProps) => {
  const dispatch = useDispatch()
  const persistClickedItem = usePersistClickedItemOnURL()

  const { clientId, patientId } = item.prescription
  const outsideSoap = !item.soapId

  const client = useSelector(getUser(clientId))
  const patient = useSelector(getPatient(patientId))
  const unifiedOrder = useSelector(getUnifiedOrder)

  const { read: SOAPReadPermissions } = useSelector(
    getCRUDByArea(PermissionArea.SOAP),
  )
  const isFoodCatalogEnabledFromStore = useSelector(
    getFeatureToggle(FeatureToggle.FOOD_CATALOG),
  )
  const isPatientSharingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.PATIENT_SHARING),
  )
  const currentBusinessId = useSelector(getCurrentBusinessId)
  const prescriptionStates = useSelector(getPrescriptionStates)
  const isContextItem = useIsCurrentContextItem(item)

  const effectiveFoodCatalogEnabled =
    isFoodCatalogEnabled ?? isFoodCatalogEnabledFromStore

  const [openRefillDialog] = useRefillDialog()
  const { fetchUnifiedOrderAndOpenPrescription } =
    useOpenPrescriptionDialogAfterFetchUnifiedOrder({
      overrideParams: {
        outsideSoap: true,
        skipOrderFetching: false,
        prescription: unifiedOrder,
      },
      prescriptionDialogProps: {
        selector: getOrdersIsSending,
      },
      isInvoice,
    })

  const openInvoice = useOpenInvoice(item.prescription.clientId)

  const updateRefill = useCallback(
    (refillOrder: Order, options: CRUDOrderOptions) => {
      const orderOptions = {
        ...options,
        outsideSoap: true,
      }

      if (outsideSoap) {
        orderOptions.invoiceId = refillOrder.invoiceId
      } else if (refillOrder.soapId) {
        orderOptions.soapId = refillOrder.soapId
      }

      dispatch(editOrder(refillOrder, orderOptions))
    },
    [dispatch, outsideSoap],
  )

  const handlePrint = useCallback(
    (prescriptionToPrint: Prescription) => {
      dispatch(
        setPrescriptionForPrint(
          timelinePrescriptionToRegularPrescription(
            {
              ...prescriptionToPrint,
              client,
              patient,
            },
            item,
            effectiveFoodCatalogEnabled,
          ),
        ),
      )
    },
    [dispatch, item, client, patient, effectiveFoodCatalogEnabled],
  )

  const handleViewInvoice = useCallback(
    (invoiceId?: string | Nil) => {
      persistClickedItem(index)
      openInvoice({
        clientId: item.prescription.clientId,
        patientId: item.prescription.patientId,
        soapId: item.soapId,
        logId: item.prescription.id,
        logType: OrderType.PRESCRIPTION,
        invoiceId,
      })
    },
    [
      item.prescription.clientId,
      item.prescription.patientId,
      item.prescription.id,
      item.soapId,
      index,
      persistClickedItem,
      openInvoice,
    ],
  )

  const handleViewRefill = useCallback(
    (refillToView: Prescription) =>
      openInvoice({
        clientId: refillToView.clientId,
        patientId: refillToView.patientId,
        logType: OrderType.PRESCRIPTION,
        logId: refillToView.id,
      }),
    [openInvoice],
  )

  /**
   * Opens the preview/edit dialog for the parent prescription
   */
  const handlePreviewPrescription = useCallback(
    () => fetchUnifiedOrderAndOpenPrescription(item.prescription.id),
    [fetchUnifiedOrderAndOpenPrescription, item.prescription.id],
  )

  const handlePreviewRefill = useCallback(
    (refillToPreview: Prescription) => {
      const refillItem = timelinePrescriptionToRegularPrescription(
        refillToPreview,
        item,
        effectiveFoodCatalogEnabled,
      )
      const { prescription: cleanPrescription, parent } =
        prepareRefillPrescription(
          refillItem,
          [], // No additional fields to omit for preview (keeps id for editing)
        )
      openRefillDialog({
        outsideSoap: true,
        prescription: {
          ...cleanPrescription,
          parent,
        },
        onSave: onUpdateRefill || updateRefill,
      })
    },
    [
      item,
      effectiveFoodCatalogEnabled,
      onUpdateRefill,
      updateRefill,
      openRefillDialog,
    ],
  )

  const handleCancelRefills = () =>
    dispatch(cancelRefills(item.prescription.id))

  const getActionVisibility = useCallback(
    (overrideRefill?: Prescription): ActionVisibility => {
      const refillToUse = overrideRefill ?? refill
      const prescriptionToUse = refillToUse ?? item.prescription
      const hasInvoice = refillToUse
        ? Boolean(refillToUse.invoiceId)
        : Boolean(getIdFromTimelineItem(item))

      const { isChewyReactiveRx, isChewyActiveRx } =
        getPrescriptionWorkflowType({
          orderType: prescriptionToUse.type,
          prescriptionType: prescriptionToUse.prescriptionType,
          origin: prescriptionToUse.origin,
        })
      const isChewy = isChewyReactiveRx || isChewyActiveRx

      const prescriptionStateName = Utils.getConstantName(
        prescriptionToUse.stateId,
        prescriptionStates,
      )
      const isPendingApproval =
        prescriptionStateName === PrescriptionStates.PENDING_VET_APPROVAL

      const isContextRefillItem = refillToUse
        ? !isPatientSharingEnabled ||
          refillToUse.businessId === currentBusinessId
        : true

      const hasSoapForRefill = refillToUse
        ? Boolean(refillToUse.soapId && SOAPReadPermissions)
        : Boolean(item.soapId && SOAPReadPermissions)

      const canShowButtonGroupActions = refillToUse
        ? isContextRefillItem
        : isContextItem &&
          !item.soapId &&
          SOAPReadPermissions &&
          !isPendingApproval

      const canShowMenu = hasSoapForRefill || canShowButtonGroupActions

      const shouldShowPrint = refillToUse
        ? canShowButtonGroupActions && showPrint
        : canShowButtonGroupActions && showPrint && !isChewy
      const shouldShowInvoice = canShowButtonGroupActions && hasInvoice
      const shouldShowPreview = canShowButtonGroupActions
      const shouldShowSoap = refillToUse ? false : hasSoapForRefill

      return {
        canShowMenu,
        shouldShowPrint,
        shouldShowInvoice,
        shouldShowPreview,
        shouldShowSoap,
        isChewy,
      }
    },
    [
      refill,
      item,
      prescriptionStates,
      isPatientSharingEnabled,
      currentBusinessId,
      SOAPReadPermissions,
      isContextItem,
      showPrint,
    ],
  )

  const getSoapUrl = useCallback(
    (soapId: string) =>
      addOriginalBusinessId(
        `/soap/${soapId}`,
        isPatientSharingEnabled ? item.businessId : null,
      ),
    [isPatientSharingEnabled, item.businessId],
  )

  return {
    handlePrint,
    handleViewInvoice,
    handleViewRefill,
    handlePreviewPrescription,
    handlePreviewRefill,
    handleCancelRefills,
    updateRefill,
    getActionVisibility,
    getSoapUrl,
    isPatientSharingEnabled,
    currentBusinessId,
    persistClickedItem,
  }
}
